import { Link, NavLink, useNavigate } from "react-router-dom";
import {
    LogIn,
    LogOut,
    MapPin,
    Search,
    ShieldCheck,
    ShoppingCart,
    User as UserIcon,
    Wallet as WalletIcon,
} from "lucide-react";
import { toast } from "sonner";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ROUTES } from "@/constants/routes";
import { useAuth } from "@/features/auth/AuthContext";
import { NotificationBell } from "@/components/notifications/NotificationBell";
import { NotificationEnableButton } from "@/components/notifications/NotificationEnableButton";
import { isAdminRole } from "@/features/auth/roles";
import { useCart } from "@/features/cart/CartContext";
import { cn } from "@/lib/utils";

const NAV_LINKS = [
    { label: "Home", to: ROUTES.home },
    { label: "Categories", to: ROUTES.categories },
    { label: "Orders", to: ROUTES.orders },
    { label: "Wallet", to: "/wallet" },
];

function initials(name: string): string {
    return name
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((p) => p[0]?.toUpperCase() ?? "")
        .join("") || "T";
}

export function Navbar() {
    const { user, isAuthenticated, logout } = useAuth();
    const { cart } = useCart();
    const totalCount = cart.total_items;
    const navigate = useNavigate();

    const onLogout = async () => {
        await logout();
        toast.success("Signed out");
        navigate(ROUTES.welcome);
    };

    return (
        <header
            data-testid="navbar"
            className="sticky top-0 z-40 w-full border-b border-border/60 bg-background/85 backdrop-blur supports-[backdrop-filter]:bg-background/70"
        >
            <div className="tsgo-container flex h-16 items-center justify-between gap-3 md:h-20">
                {/* Brand + location */}
                <div className="flex items-center gap-3 md:gap-6">
                    <Link
                        to={ROUTES.home}
                        data-testid="navbar-brand"
                        className="flex items-center gap-2"
                    >
                        <span className="grid h-9 w-9 place-items-center rounded-xl bg-brand text-white shadow-brand">
                            <span className="text-lg font-extrabold leading-none">
                                T
                            </span>
                        </span>
                        <span className="hidden text-xl font-extrabold tracking-tight text-foreground sm:inline">
                            TSGo
                        </span>
                    </Link>

                    <button
                        type="button"
                        data-testid="navbar-location"
                        className="hidden items-center gap-2 rounded-full border border-border/70 bg-muted/40 px-3 py-1.5 text-left text-sm font-medium text-foreground/80 transition hover:bg-muted md:inline-flex"
                    >
                        <MapPin className="h-4 w-4 text-brand" />
                        <span className="flex flex-col leading-tight">
                            <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                                Deliver to
                            </span>
                            <span className="text-sm">Set your location</span>
                        </span>
                    </button>
                </div>

                {/* Center nav (desktop) */}
                <nav className="hidden items-center gap-1 md:flex">
                    {NAV_LINKS.map((link) => (
                        <NavLink
                            key={link.to}
                            to={link.to}
                            data-testid={`navbar-link-${link.label.toLowerCase()}`}
                            className={({ isActive }) =>
                                cn(
                                    "rounded-full px-4 py-2 text-sm font-medium transition",
                                    isActive
                                        ? "bg-brand/10 text-brand"
                                        : "text-foreground/70 hover:bg-muted hover:text-foreground",
                                )
                            }
                        >
                            {link.label}
                        </NavLink>
                    ))}
                </nav>

                {/* Actions */}
                <div className="flex items-center gap-1.5 md:gap-2">
                    <Link
                        to={ROUTES.search}
                        data-testid="navbar-search"
                        aria-label="Search"
                        className="inline-flex h-10 w-10 items-center justify-center rounded-full text-foreground/70 transition hover:bg-muted hover:text-foreground"
                    >
                        <Search className="h-5 w-5" />
                    </Link>
                    <Link
                        to={ROUTES.cart}
                        data-testid="navbar-cart"
                        aria-label="Cart"
                        className="relative inline-flex h-10 w-10 items-center justify-center rounded-full text-foreground/70 transition hover:bg-muted hover:text-foreground"
                    >
                        <ShoppingCart className="h-5 w-5" />
                        {totalCount > 0 ? (
                            <span
                                aria-hidden
                                data-testid="navbar-cart-count"
                                className="absolute -right-0.5 -top-0.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-brand px-1 text-[10px] font-bold text-white"
                            >
                                {totalCount}
                            </span>
                        ) : null}
                    </Link>

                    {isAuthenticated ? (
                        <>
                            <NotificationEnableButton />
                            <NotificationBell />
                        </>
                    ) : null}

                    {isAuthenticated && user ? (
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <button
                                    type="button"
                                    data-testid="navbar-user-menu"
                                    className="inline-flex h-10 items-center gap-2 rounded-full border border-border/70 bg-white px-1.5 pr-3 text-sm font-semibold text-foreground transition hover:border-brand/40 hover:bg-brand/5"
                                >
                                    <span className="grid h-7 w-7 place-items-center rounded-full bg-brand text-[11px] font-extrabold text-white">
                                        {initials(user.full_name)}
                                    </span>
                                    <span className="hidden max-w-[110px] truncate sm:inline">
                                        {user.full_name.split(" ")[0]}
                                    </span>
                                </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-56">
                                <DropdownMenuLabel className="flex flex-col">
                                    <span className="text-sm font-semibold">
                                        {user.full_name}
                                    </span>
                                    <span className="truncate text-xs font-normal text-muted-foreground">
                                        {user.email}
                                    </span>
                                </DropdownMenuLabel>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                    onClick={() => navigate(ROUTES.profile)}
                                    data-testid="navbar-menu-profile"
                                >
                                    <UserIcon className="mr-2 h-4 w-4" /> Profile
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                    onClick={() => navigate(ROUTES.orders)}
                                    data-testid="navbar-menu-orders"
                                >
                                    <ShoppingCart className="mr-2 h-4 w-4" /> Orders
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                    onClick={() => navigate("/wallet")}
                                    data-testid="navbar-menu-wallet"
                                >
                                    <WalletIcon className="mr-2 h-4 w-4" /> TSGo Wallet
                                </DropdownMenuItem>
                                {isAdminRole(user.role) ? (
                                    <>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem
                                            onClick={() => navigate(ROUTES.adminDashboard)}
                                            data-testid="navbar-menu-admin"
                                            className="font-semibold text-brand focus:text-brand"
                                        >
                                            <ShieldCheck className="mr-2 h-4 w-4" /> Admin panel
                                        </DropdownMenuItem>
                                    </>
                                ) : null}
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                    onClick={onLogout}
                                    data-testid="navbar-menu-logout"
                                    className="text-error focus:text-error"
                                >
                                    <LogOut className="mr-2 h-4 w-4" /> Sign out
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    ) : (
                        <Link
                            to={ROUTES.login}
                            data-testid="navbar-signin"
                            className="inline-flex h-10 items-center gap-1.5 rounded-full bg-brand px-4 text-sm font-semibold text-white transition hover:bg-brand-600"
                        >
                            <LogIn className="h-4 w-4" />
                            <span className="hidden sm:inline">Sign in</span>
                        </Link>
                    )}
                </div>
            </div>
        </header>
    );
}

export default Navbar;
